"use client";

import * as React from "react";
import {
  motion,
  type TargetAndTransition,
  type Transition,
  useReducedMotion,
} from "framer-motion";

interface ISafeMotionDivProps {
  children: React.ReactNode;
  /** Changing this remounts the div so the `initial → animate` enter transition replays. */
  motionKey: string;
  className?: string;
  initial: TargetAndTransition;
  animate: TargetAndTransition;
  transition: Transition;
}

/**
 * Keyed enter-only page transition.
 *
 * Deliberately does NOT use `AnimatePresence`. Two production crashes traced
 * back to it on React 19 + Turbopack:
 *  1. "TypeError: null is not an object (evaluating 'b.parentNode')" on iOS
 *     Safari — exit animations measuring DOM nodes that were already removed.
 *  2. "Rendered more hooks than during the previous render." (Sentry
 *     TIPPO-DEV-M) — `AnimatePresence` calls `useContext(LayoutGroupContext)`
 *     *after* an in-render `return null` diffing branch. Under React 19
 *     concurrent rendering that render-phase-update dance can commit the
 *     shorter hook list, so the follow-up render trips React's hook-count
 *     guard. A keyless direct child (the previous bug) made the diffing branch
 *     fire on every navigation, maximizing the odds.
 *
 * Exit animations are the only thing `AnimatePresence` buys us, and both call
 * sites had already neutralized them. So we drop it: a `key` change remounts
 * the element and replays the enter animation — which is the behaviour users
 * actually saw — with none of the fragility.
 */
export function SafeMotionDiv({
  children,
  motionKey,
  className,
  initial,
  animate,
  transition,
}: ISafeMotionDivProps): React.ReactElement {
  // `useReducedMotion()` is stable for the lifetime of the instance, so this
  // branch is consistent across renders (no rules-of-hooks violation).
  const prefersReducedMotion = useReducedMotion();

  if (prefersReducedMotion) {
    return (
      <div key={motionKey} className={className}>
        {children}
      </div>
    );
  }

  return (
    <motion.div
      key={motionKey}
      className={className}
      initial={initial}
      animate={animate}
      transition={transition}
    >
      {children}
    </motion.div>
  );
}
